import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Newspaper } from "lucide-react";
import DOMPurify from "dompurify";
import { Navbar } from "@/components/landing/Navbar";
import { getNews } from "@/lib/api";

const NewsCard = ({ n, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.4, delay: index * 0.05 }}
    data-testid={`news-card-${n.id}`}
    className="rounded-2xl border border-white/5 bg-[#12141D] p-6 transition-colors duration-300 hover:border-[#D4AF37]/30"
  >
    <div className="flex items-center gap-3">
      {n.author_avatar ? (
        <img src={n.author_avatar} alt="" loading="lazy" className="h-9 w-9 rounded-full object-cover ring-1 ring-white/10" />
      ) : (
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#8A1538]/20 text-xs font-bold text-[#D4AF37]">
          {(n.author_name || "?").charAt(0)}
        </span>
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-[#FDFBF7]">{n.author_name || "Unknown"}</p>
        <p className="font-mono-data text-[11px] text-[#A1A1AA]">{n.author_ident} · {n.created_at}</p>
      </div>
    </div>
    <h2 className="mt-4 font-display text-xl font-bold text-[#FDFBF7]">{n.subject}</h2>
    <div
      className="mt-2 text-sm text-[#A1A1AA] [text-wrap:pretty] [&_p]:mb-3 [&_p:last-child]:mb-0 [&_a]:text-[#D4AF37] [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-[#FDFBF7]"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(n.message || "") }}
    />
  </motion.article>
);

export default function News() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getNews()
      .then((r) => setItems(r?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0B10]" data-testid="news-page">
      <Navbar />
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-28 lg:px-10 lg:pt-32">
        <div className="max-w-2xl">
          <p className="text-overline flex items-center gap-2 text-[#8A1538]">
            <Newspaper className="h-4 w-4" /> Newsroom
          </p>
          <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-[#FDFBF7] lg:text-5xl [text-wrap:balance]">
            Latest from the airline
          </h1>
        </div>

        <div className="mt-10 space-y-5">
          {loading ? (
            <p className="py-16 text-center text-sm text-[#A1A1AA]">Loading news…</p>
          ) : items.length === 0 ? (
            <p className="py-16 text-center text-sm text-[#A1A1AA]" data-testid="news-empty">
              No news posted yet.
            </p>
          ) : (
            items.map((n, i) => <NewsCard key={n.id} n={n} index={i} />)
          )}
        </div>
      </main>
    </div>
  );
}
